import React, { useEffect, useState } from "react";
import { leaveApi } from "../../services/leaveApi";
import { StatusBadge } from "../StatusBadge";

/** Leave tab - current balance summary, mandatory-use progress, grant expirations, and the
 * full grant/use/adjustment ledger for this employee (Payroll & Leave Deep Audit item 26). */
export const LeaveTab = ({ employeeId }) => {
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [mandatoryUse, setMandatoryUse] = useState([]);
  const [grants, setGrants] = useState([]);

  useEffect(() => {
    leaveApi.employeeBalance(employeeId).then((r) => setBalance(r.data)).catch(() => setBalance(null));
    leaveApi.employeeLedger(employeeId).then((r) => setLedger(r.data)).catch(() => setLedger([]));
    leaveApi.employeeMandatoryUse(employeeId).then((r) => setMandatoryUse(r.data)).catch(() => setMandatoryUse([]));
    leaveApi.listGrants(employeeId).then((r) => setGrants(r.data)).catch(() => setGrants([]));
  }, [employeeId]);

  const expiringGrants = grants.filter((g) => g.expires_at);

  return (
    <div className="space-y-4" data-testid="employee-leave-tab">
      {balance && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-slate-900" data-testid="leave-current-balance">{balance.current_balance}</p><p className="text-[11px] text-slate-500 uppercase">Current balance (days)</p></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-slate-900">{balance.granted_total}</p><p className="text-[11px] text-slate-500 uppercase">Granted total</p></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-slate-900">{balance.used_total}</p><p className="text-[11px] text-slate-500 uppercase">Used total</p></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-slate-900">{balance.future_reserved_total}</p><p className="text-[11px] text-slate-500 uppercase">Future reserved</p></div>
        </div>
      )}

      {mandatoryUse.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="employee-mandatory-use-panel">
          <p className="text-[11px] text-slate-500 uppercase mb-2">Mandatory-Use Progress</p>
          <div className="space-y-2">
            {mandatoryUse.map((m) => (
              <div key={m.grant_id} className="flex items-center justify-between text-sm" data-testid={`employee-mandatory-use-row-${m.grant_id}`}>
                <span className="text-slate-600">{m.monitoring_start} &rarr; {m.monitoring_end}</span>
                <span className="text-slate-500">Used {m.used_amount}/{m.required_use_total} (remaining {m.remaining_mandatory_amount})</span>
                <StatusBadge status={m.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {expiringGrants.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="employee-grant-expiration-panel">
          <p className="text-[11px] text-slate-500 uppercase mb-2">Grant Expiration</p>
          <div className="space-y-1">
            {expiringGrants.map((g) => (
              <div key={g.id} className="flex items-center justify-between text-sm" data-testid={`employee-grant-expiration-row-${g.id}`}>
                <span className="text-slate-600">{g.days_granted} days granted {g.grant_date}</span>
                <span className="text-slate-500">Expires {g.expires_at}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium">Type</th>
              <th className="px-3 py-2 font-medium">Days</th>
              <th className="px-3 py-2 font-medium">Reason</th>
            </tr>
          </thead>
          <tbody>
            {(ledger || []).map((t) => (
              <tr key={t.id} className="border-t border-slate-100" data-testid={`leave-transaction-row-${t.id}`}>
                <td className="px-3 py-2 font-medium text-slate-700">{t.effective_date}</td>
                <td className="px-3 py-2 text-slate-500">{t.type}</td>
                <td className={`px-3 py-2 font-semibold ${t.days >= 0 ? "text-emerald-600" : "text-red-500"}`}>{t.days > 0 ? "+" : ""}{t.days}</td>
                <td className="px-3 py-2 text-slate-500 max-w-[240px] truncate" title={t.reason}>{t.reason || "-"}</td>
              </tr>
            ))}
            {ledger && ledger.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-500" data-testid="no-leave-transactions-row">No leave transactions yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

